import { api } from './api'

/**
 * #4 Camera/QR self-diagnostics.
 *
 * Managers can't report why a scanner failed — the real reason lives in the browser
 * (permission state, secure context, the library's error). This collects that snapshot
 * so a failure explains itself, instead of surfacing as «Камера недоступна».
 */

export interface CameraDiagnostics {
  /** Human summary shown on screen */
  summary: string[]
  /** Raw payload sent to the server */
  payload: Record<string, unknown>
  /** true when nothing blocking was found */
  looksOk: boolean
}

function displayMode(): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any
  if (w.Capacitor?.isNativePlatform?.()) return 'APK (Capacitor)'
  if (w.navigator?.standalone) return 'PWA (иконка с экрана «Домой», iOS)'
  if (window.matchMedia?.('(display-mode: standalone)')?.matches) return 'PWA (иконка с экрана «Домой»)'
  return 'Браузер'
}

async function permissionState(): Promise<string> {
  try {
    // Not supported on some iOS versions — that's itself useful to know.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res = await (navigator as any).permissions?.query({ name: 'camera' })
    return res?.state ?? 'не поддерживается браузером'
  } catch {
    return 'не поддерживается браузером'
  }
}

async function videoInputs(): Promise<{ count: number; labels: string[] }> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const cams = devices.filter((d) => d.kind === 'videoinput')
    return { count: cams.length, labels: cams.map((c) => c.label || '(без названия — нет разрешения)') }
  } catch {
    return { count: -1, labels: [] }
  }
}

/**
 * Runs the checks. `probe` actually asks for the camera — use it on the diagnostics
 * screen (an explicit user action), not silently in the background.
 */
export async function collectCameraDiagnostics(opts?: {
  probe?: boolean
  entryPoint?: string
  scannerError?: unknown
}): Promise<CameraDiagnostics> {
  const secure = window.isSecureContext
  const hasMediaDevices = !!navigator.mediaDevices?.getUserMedia
  const perm = await permissionState()
  const cams = await videoInputs()
  const mode = displayMode()

  let probeResult: string | null = null
  let probeErrorName: string | null = null
  let probeErrorMessage: string | null = null
  if (opts?.probe && hasMediaDevices) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      stream.getTracks().forEach((t) => t.stop())
      probeResult = 'камера открылась успешно'
    } catch (e) {
      const err = e as { name?: string; message?: string }
      probeErrorName = err?.name ?? 'Error'
      probeErrorMessage = err?.message ?? String(e)
      probeResult = `отказ: ${probeErrorName}`
    }
  }

  const scanErr = opts?.scannerError as { name?: string; message?: string } | undefined
  const scannerErrorName = scanErr?.name ?? (opts?.scannerError ? 'Error' : null)
  const scannerErrorMessage = scanErr?.message ?? (opts?.scannerError ? String(opts.scannerError) : null)

  const problems: string[] = []
  if (!secure) problems.push('Соединение не защищённое (нужен HTTPS) — браузер не даст камеру')
  if (!hasMediaDevices) problems.push('Браузер не поддерживает доступ к камере')
  if (perm === 'denied') problems.push('Доступ к камере ЗАПРЕЩЁН в настройках — нужно разрешить и перезайти')
  if (cams.count === 0) problems.push('Устройство не видит ни одной камеры')

  const summary = [
    `Устройство/браузер: ${navigator.userAgent}`,
    `Режим запуска: ${mode}`,
    `Защищённое соединение (HTTPS): ${secure ? 'да' : 'НЕТ'}`,
    `Доступ к камере: ${perm}`,
    `Камер найдено: ${cams.count < 0 ? 'не удалось определить' : cams.count}`,
    ...(cams.labels.length ? [`Камеры: ${cams.labels.join(', ')}`] : []),
    ...(probeResult ? [`Проверка камеры: ${probeResult}`] : []),
    ...(probeErrorMessage ? [`Текст ошибки: ${probeErrorMessage}`] : []),
    ...(scannerErrorMessage ? [`Ошибка сканера: ${scannerErrorName} — ${scannerErrorMessage}`] : []),
    ...(problems.length ? ['', 'НАЙДЕНО:', ...problems.map((p) => '• ' + p)] : ['', 'Явных блокировок не найдено']),
  ]

  return {
    summary,
    looksOk: problems.length === 0,
    payload: {
      userAgent: navigator.userAgent,
      displayMode: mode,
      isSecureContext: secure,
      hasMediaDevices,
      permission: perm,
      cameraCount: cams.count,
      cameraLabels: cams.labels,
      entryPoint: opts?.entryPoint ?? window.location.pathname,
      probeResult,
      probeErrorName,
      probeErrorMessage,
      scannerErrorName,
      scannerErrorMessage,
      screen: `${window.screen?.width}x${window.screen?.height}`,
      at: new Date().toISOString(),
    },
  }
}

/** Fire-and-forget: a scanner failure reports itself so nobody has to describe it. */
export async function reportScanFailure(opts: { entryPoint?: string; scannerError?: unknown }): Promise<void> {
  try {
    const diag = await collectCameraDiagnostics({ probe: false, ...opts })
    await api.post('/diagnostics/scan', diag.payload)
  } catch {
    /* diagnostics must never break the scanner */
  }
}
